import {
  getDoc,
  getDocs,
  collection,
  doc,
} from "@/services/legacyBrowserPersistenceBoundary";

const canUseLegacyOrganisationAggregate = () => false;

const readCollectionData = async (...segments) => {
  const snapshot = await getDocs(collection(null, ...segments));
  return {
    documents: snapshot.docs.map((documentSnapshot) => documentSnapshot.data()),
    size: snapshot.size,
  };
};

const buildBasicOrgDetails = (organisationData, membersCount) => ({
  organisationName: organisationData.name,
  totalOrgCoins: organisationData.totalOrgCoins,
  description: organisationData.description,
  category: organisationData.category,
  basicIncome: organisationData.monetary_parameters?.basic_income,
  regularity: organisationData.monetary_parameters?.regularity,
  initialCoins: organisationData.monetary_parameters?.initialCoins,
  coinName: organisationData.monetary_parameters?.coin_name,
  coinSymbol: organisationData.monetary_parameters?.coin_symbol,
  logo: organisationData.logo,
  baseFounderCompensation: 0,
  currentAccountCap: organisationData.monetary_parameters?.current_account?.cap,
  currentAccountLifespan:
    organisationData.monetary_parameters?.current_account?.lifespan,
  savingsAccountCap: organisationData.monetary_parameters?.savings_account?.cap,
  allowSavings:
    organisationData.monetary_parameters?.savings_account?.allowSavings,
  membersCount,
  loans: organisationData.monetary_parameters?.loans || {
    dti_effortRate: 0,
    maxLoanTerm: 0,
    annualInterest: 0,
    allowIndividual: false,
    allowJoint: false,
  },
});

const calculateAverageBaseCompensation = (roles) => {
  if (roles.length === 0) return 0;
  const totalBaseCompensation = roles.reduce((total, role) => {
    const baseCompensation = Number(role.baseCompensation);
    return total + (Number.isFinite(baseCompensation) ? baseCompensation : 0);
  }, 0);
  return totalBaseCompensation / roles.length;
};

const normaliseFetchAllPayload = (payload) => {
  const orgUID =
    typeof payload === "string" ? payload : payload?.orgUID || "";
  const requestKey =
    typeof payload === "object" && payload?.requestKey
      ? payload.requestKey
      : `organisation:${orgUID}`;
  return { orgUID, requestKey };
};

const readAllOrganisationData = async (orgUID) => {
  const [organisationSnapshot, rolesResult, membersResult, stakeholdersResult] =
    await Promise.all([
      getDoc(doc(null, "custom-orgs", orgUID)),
      readCollectionData("custom-orgs", orgUID, "Roles").catch(() => ({
        documents: [],
        size: 0,
      })),
      readCollectionData("custom-orgs", orgUID, "Members"),
      readCollectionData("custom-orgs", orgUID, "Stakeholders").catch(() => ({
        documents: [],
        size: 0,
      })),
    ]);

  const roles = rolesResult.documents;
  return {
    basicOrgDetails: organisationSnapshot.exists()
      ? buildBasicOrgDetails(
          organisationSnapshot.data(),
          membersResult.size ?? membersResult.documents.length,
        )
      : null,
    roles,
    members: membersResult.documents,
    stakeholders: stakeholdersResult.documents,
    averageBaseCompensation: calculateAverageBaseCompensation(roles),
  };
};

export default {
  state: {
    organisations: {},
    roles: null,
    roleData: null,
    members: null,
    stakeholders: null,
    averageBaseCompensation: null,
    organisationDataOwnerUID: null,
    organisationDataRequests: {},
    organisationDataRequestGeneration: 0,
  },
  getters: {
    getBasicOrgDetails: (state) => (orgUID) =>
      state.organisations[orgUID]?.basicOrgDetails || null,
    getRoles: (state) => state.roles,
    getRoleData: (state) => state.roleData,
    getMembers: (state) => state.members,
    getStakeholders: (state) => state.stakeholders,
    getAverageBaseCompensation: (state) => state.averageBaseCompensation,
    getRolesForOrganisation: (state) => (orgUID) =>
      state.organisations[orgUID]?.roles || null,
    getMembersForOrganisation: (state) => (orgUID) =>
      state.organisations[orgUID]?.members || null,
    getStakeholdersForOrganisation: (state) => (orgUID) =>
      state.organisations[orgUID]?.stakeholders || null,
    getAverageBaseCompensationForOrganisation: (state) => (orgUID) =>
      state.organisations[orgUID]?.averageBaseCompensation ?? null,
  },
  mutations: {
    BEGIN_ORGANISATION_DATA_REQUEST(
      state,
      { orgUID, requestKey, generation },
    ) {
      state.organisationDataRequestGeneration = generation;
      state.organisationDataRequests[requestKey] = { orgUID, generation };
    },
    COMMIT_ORGANISATION_DATA(
      state,
      { orgUID, requestKey, generation, organisationData },
    ) {
      const activeRequest = state.organisationDataRequests[requestKey];
      if (
        activeRequest?.orgUID !== orgUID ||
        activeRequest?.generation !== generation
      ) {
        return;
      }

      if (!state.organisations[orgUID]) {
        state.organisations[orgUID] = {};
      }
      Object.assign(state.organisations[orgUID], organisationData);
      state.roles = organisationData.roles;
      state.members = organisationData.members;
      state.stakeholders = organisationData.stakeholders;
      state.averageBaseCompensation =
        organisationData.averageBaseCompensation;
      state.organisationDataOwnerUID = orgUID;
      delete state.organisationDataRequests[requestKey];
    },
    FAIL_ORGANISATION_DATA_REQUEST(
      state,
      { orgUID, requestKey, generation },
    ) {
      const activeRequest = state.organisationDataRequests[requestKey];
      if (
        activeRequest?.orgUID === orgUID &&
        activeRequest?.generation === generation
      ) {
        delete state.organisationDataRequests[requestKey];
      }
    },
    SET_EXTRA_ORG_DETAILS(state, { orgUID, payload }) {
      if (!state.organisations[orgUID]) {
        state.organisations[orgUID] = {};
      }
      state.organisations[orgUID].basicOrgDetails = payload;
    },
    SET_ROLE_DATA(state, payload) {
      state.roleData = payload;
    },
    CLEAR_ROLE_DATA(state) {
      state.roleData = null;
    },
    CLEAR_ALL_STATE(state) {
      state.organisations = {};
      state.roles = null;
      state.roleData = null;
      state.members = null;
      state.stakeholders = null;
      state.averageBaseCompensation = null;
      state.organisationDataOwnerUID = null;
      state.organisationDataRequests = {};
      state.organisationDataRequestGeneration += 1;
    },
  },
  actions: {
    async fetchAllData({ state, commit }, payload) {
      const { orgUID, requestKey } = normaliseFetchAllPayload(payload);
      const generation = state.organisationDataRequestGeneration + 1;
      commit("BEGIN_ORGANISATION_DATA_REQUEST", {
        orgUID,
        requestKey,
        generation,
      });

      if (!canUseLegacyOrganisationAggregate()) {
        commit("FAIL_ORGANISATION_DATA_REQUEST", {
          orgUID,
          requestKey,
          generation,
        });
        return null;
      }

      try {
        const organisationData = await readAllOrganisationData(orgUID);
        const activeRequest = state.organisationDataRequests[requestKey];
        if (
          activeRequest?.orgUID !== orgUID ||
          activeRequest?.generation !== generation
        ) {
          return null;
        }
        commit("COMMIT_ORGANISATION_DATA", {
          orgUID,
          requestKey,
          generation,
          organisationData,
        });
        return organisationData;
      } catch {
        commit("FAIL_ORGANISATION_DATA_REQUEST", {
          orgUID,
          requestKey,
          generation,
        });
        return null;
      }
    },
    async fetchBasicOrgDetails({ commit }, orgUID) {
      if (!canUseLegacyOrganisationAggregate()) return null;
      try {
        const [organisationSnapshot, membersResult] = await Promise.all([
          getDoc(doc(null, "custom-orgs", orgUID)),
          readCollectionData("custom-orgs", orgUID, "Members"),
        ]);
        if (!organisationSnapshot.exists()) return null;

        const basicOrgDetails = buildBasicOrgDetails(
          organisationSnapshot.data(),
          membersResult.size ?? membersResult.documents.length,
        );
        commit("SET_EXTRA_ORG_DETAILS", {
          orgUID,
          payload: basicOrgDetails,
        });
        return basicOrgDetails;
      } catch (error) {
        return null;
      }
    },
    async fetchRoleData({ commit }, { orgUID, roleUID }) {
      if (!canUseLegacyOrganisationAggregate()) return null;
      try {
        const roleDocument = await getDoc(
          doc(null, "custom-orgs", orgUID, "Roles", roleUID),
        );
        if (!roleDocument.exists()) return null;

        const roleData = roleDocument.data();
        commit("SET_ROLE_DATA", roleData);
        return roleData;
      } catch (error) {
        return null;
      }
    },
    async clearAllState({ commit }) {
      commit("CLEAR_ALL_STATE");
    },
  },
};
